import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  AlertTriangle,
  Barcode,
  CircleDot,
  Eye,
  ImageOff,
  LayoutGrid,
  Link2,
  List,
  Repeat,
  RotateCcw,
  Search,
  Upload
} from "lucide-react";
import {
  useGetV4ManufacturerByRouteManufacturerIdAccountQuery,
  useGetV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeQuery,
  usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation,
  usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeImageMutation,
  usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation,
} from "randmar-api-client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ActiveOrdersCard,
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  OrdersCard,
  ProductCard,
  RmBodyZone,
  SalesChart,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  formatMoney,
  useToast,
} from "randmarcomps";
import { type ComponentProps, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NewMarginsGrid from "../components/grids/NewMarginsGrid";
import DataRefreshAssistantCluster from "../components/layout/DataRefreshAssistantCluster";
import RmPageHeader from "../components/layout/RmPageHeader";
import ProductActions from "../components/products/ProductActions";
import InlineSpinner from "../components/util/InlineSpinner";
import Path from "../routes/Path";
import { Product } from "../stores/product/Product";

const CategoryImagePlaceholder = () => (
  <div
    className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 text-slate-500"
    aria-label="Image not available"
  >
    <ImageOff className="h-12 w-12" />
    <p className="mt-2 text-sm">No Image</p>
  </div>
);

const ProductTableImage = ({ sku, alt }: { sku: string; alt: string }) => {
  const [hasError, setHasError] = useState(false);
  const timestamp = useRef(new Date().getTime());
  const imageUrl = `${Path.productImage(sku)}?t=${timestamp.current}`;

  if (hasError || !sku) {
    return (
      <div className="h-12 w-12 flex-shrink-0 bg-muted rounded-sm flex items-center justify-center text-muted-foreground">
        <ImageOff className="h-6 w-6" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className="h-12 w-12 flex-shrink-0 rounded-sm object-contain"
      onError={() => setHasError(true)}
    />
  );
};

const integerFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

const safeNumber = (value: number) => (Number.isFinite(value) ? value : 0);

const formatCount = (value: number) => integerFormatter.format(Math.round(safeNumber(value)));

const formatPlural = (value: number, noun: string) => {
  const rounded = Math.round(safeNumber(value));
  return `${formatCount(rounded)} ${noun}${rounded === 1 ? "" : "s"}`;
};

type CategorySalesMetrics = {
  lifetimeRevenue: number;
  lifetimeUnits: number;
  avgDailyRevenue: number;
  lastSaleDate?: Date | null;
  bestDayRevenue: number;
  bestDayDate?: Date | null;
  last30Revenue: number;
  last30Units: number;
  daysTracked: number;
};

const dateFormatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" });

const parseStatisticDate = (day?: number) => {
  if (!day && day !== 0) return null;
  const dayString = day.toString().padStart(8, "0");
  const year = Number(dayString.slice(0, 4));
  const month = Number(dayString.slice(4, 6)) - 1;
  const date = Number(dayString.slice(6, 8));
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(date)) return null;
  return new Date(Date.UTC(year, month, date));
};

const formatDateLabel = (date?: Date | null) => (date ? dateFormatter.format(date) : "—");

type BadgeVariant = ComponentProps<typeof Badge>["variant"];

const productChipClasses = "h-7 w-7 justify-center rounded-full p-0 [&>span]:mr-0";

const parseNumericFilterValue = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const ProductValueChip = ({
  variant,
  icon,
  label,
  tooltip,
}: {
  variant: BadgeVariant;
  icon: ReactNode;
  label: string;
  tooltip: string;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span tabIndex={0} className="inline-flex">
        <Badge variant={variant} className={productChipClasses} icon={icon} aria-label={label}>
          <span className="sr-only">{label}</span>
        </Badge>
      </span>
    </TooltipTrigger>
    <TooltipContent>{tooltip}</TooltipContent>
  </Tooltip>
);

const resolveStateChip = (state?: string | null): { variant: BadgeVariant; tooltip: string } => {
  const trimmed = state?.trim();
  if (!trimmed) return { variant: "outline", tooltip: "State: —" };

  const normalized = trimmed.toLowerCase();
  if (normalized.includes("active") || normalized.includes("available")) return { variant: "success", tooltip: `State: ${trimmed}` };
  if (normalized.includes("hold") || normalized.includes("pending")) return { variant: "warning", tooltip: `State: ${trimmed}` };
  if (normalized.includes("discontinued") || normalized.includes("inactive") || normalized.includes("disabled")) {
    return { variant: "destructive", tooltip: `State: ${trimmed}` };
  }

  return { variant: "secondary", tooltip: `State: ${trimmed}` };
};

function buildCategoryAssistantPrompt(options: {
  applicationId: string;
  categoryCode: string;
  categoryName: string;
  totalProducts: number;
  visibleProducts: number;
  productSearchTerm: string;
  stateFilter: string;
  costMin: string;
  costMax: string;
  stockOnly: boolean;
  sortOption: string;
  productView: "grid" | "table";
}) {
  const search = options.productSearchTerm.trim();
  const state = options.stateFilter === "all" ? "All states" : options.stateFilter;
  const costRange = [options.costMin.trim(), options.costMax.trim()].filter(Boolean).join("–");

  return [
    "You are assisting with the Randmar Manufacturer Portal Category page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Category: ${options.categoryName} (${options.categoryCode})`,
    `Products loaded: ${options.totalProducts}`,
    `Products visible (after filtering): ${options.visibleProducts}`,
    `Current view: ${options.productView}, sort=${options.sortOption}`,
    `Current filters: state=${state}${search ? `, search="${search}"` : ""}${costRange ? `, cost=${costRange}` : ""}${options.stockOnly ? ", stock=in-stock only" : ""}`,
    "",
    "Help me understand or improve what I'm seeing on this page.",
  ].join("\n");
}

export default function CategoryPage() {
  const { applicationId, categoryCode } = useParams<{ applicationId: string; categoryCode: string }>();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data: account,
    isLoading: isAccountLoading,
  } = useGetV4ManufacturerByRouteManufacturerIdAccountQuery(
    { routeManufacturerId: applicationId! },
    { skip: !applicationId }
  );

  const {
    data: category,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeQuery(
    { routeManufacturerId: applicationId!, categoryCode: categoryCode! },
    { skip: !categoryCode || !applicationId }
  );

  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageExists, setImageExists] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [activeTab, setActiveTab] = useState("statistics");
  const [productView, setProductView] = useState<'grid' | 'table'>('grid');
  const [sortOption, setSortOption] = useState<string>("percentile");
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [costMin, setCostMin] = useState("");
  const [costMax, setCostMax] = useState("");
  const [stockOnly, setStockOnly] = useState(false);
  const [officialLinkDialogOpen, setOfficialLinkDialogOpen] = useState(false);
  const [officialLinkDraft, setOfficialLinkDraft] = useState("");
  const [officialLinkProduct, setOfficialLinkProduct] = useState<{ productNumber: string; title: string } | null>(null);

  const [formData, setFormData] = useState({
    CategoryName: category?.CategoryName ?? '',
    Niche: category?.Niche ?? '',
    Starred: category?.Starred ?? false,
    AutoReorder: category?.AutoReorder ?? false,
    QualificationRequired: category?.QualificationRequired ?? false,
    MinimumOrder: category?.MinimumOrder ?? null,
    LeadTime: category?.LeadTime ?? null,
    WeeksOfStocking: category?.WeeksOfStocking ?? null,
  });

  const [updateProductField, { isLoading: isOfficialLinkUpdating }] =
    usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation();

  const activeOrders = useMemo(() => category?.ActiveOrderDetails ?? [], [category]);
  const products = useMemo(() => category?.Products ?? [], [category]);
  const statistics = useMemo(() => category?.SalesStatistics ?? [], [category]);
  const operationValues = useMemo(() => {
    if (!category?.InventoryValue?.length) return null;
    const data = category.InventoryValue;
    const totals = data.reduce(
      (acc, v) => {
        acc.inventory += v.InventoryValue || 0;
        acc.purchaseOrder += v.PurchaseOrderValue || 0;
        acc.salesOrder += v.SalesOrderValue || 0;
        return acc;
      },
      { inventory: 0, purchaseOrder: 0, salesOrder: 0 }
    );
    return { totals, byLocation: data };
  }, [category?.InventoryValue]);

  const hasCustomReorderSettings = useMemo(
    () =>
      formData.MinimumOrder != null || formData.LeadTime != null || formData.WeeksOfStocking != null,
    [formData.MinimumOrder, formData.LeadTime, formData.WeeksOfStocking]
  );

  const stateFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    products.forEach(product => {
      const trimmed = product.State?.trim();
      if (trimmed) seen.add(trimmed);
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const resetProductFilters = useCallback(() => {
    setProductSearchTerm("");
    setStateFilter("all");
    setCostMin("");
    setCostMax("");
    setStockOnly(false);
  }, []);

  // Memoized sorted products for the grid view
  const sortedProducts = useMemo(() => {
    const productsCopy = [...products];

    return productsCopy.sort((a, b) => {
      switch (sortOption) {
        case "mpn-asc":
          return (a.MPN ?? "").localeCompare(b.MPN ?? "");
        case "mpn-desc":
          return (b.MPN ?? "").localeCompare(a.MPN ?? "");
        case "price-asc": {
          const priceA = a.MAP ?? Number.POSITIVE_INFINITY;
          const priceB = b.MAP ?? Number.POSITIVE_INFINITY;
          return priceA - priceB;
        }
        case "price-desc": {
          const priceA = a.MAP ?? Number.NEGATIVE_INFINITY;
          const priceB = b.MAP ?? Number.NEGATIVE_INFINITY;
          return priceB - priceA;
        }
        case "name":
          return (a.Title ?? "").localeCompare(b.Title ?? "");
        case "percentile":
          return (b.OverallPercentileRank ?? 0) - (a.OverallPercentileRank ?? 0);
        default:
          return 0;
      }
    });
  }, [products, sortOption]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = productSearchTerm.trim().toLowerCase();
    const minCost = parseNumericFilterValue(costMin);
    const maxCost = parseNumericFilterValue(costMax);
    const normalizedStateFilter = stateFilter.trim().toLowerCase();

    return sortedProducts.filter(product => {
      const matchesSearch =
        normalizedSearch.length === 0 ||
        [product.Title, product.MPN, product.RandmarSKU, product.UPC]
          .some(value => (value || "").toLowerCase().includes(normalizedSearch));

      const matchesState =
        normalizedStateFilter === "all" ||
        (product.State || "").trim().toLowerCase() === normalizedStateFilter;

      const totalInventory = (product.Distribution?.Inventory ?? []).reduce(
        (sum, warehouseInventory) => sum + (warehouseInventory?.AvailableQuantity || 0),
        0
      );
      const matchesStock = !stockOnly || totalInventory > 0;

      if (!matchesSearch || !matchesState || !matchesStock) return false;

      if (minCost == null && maxCost == null) return true;

      const rawCost = product.Distribution?.Cost;
      const cost = typeof rawCost === "number" && Number.isFinite(rawCost) ? rawCost : null;
      if (cost == null) return false;

      if (minCost != null && cost < minCost) return false;
      if (maxCost != null && cost > maxCost) return false;
      return true;
    });
  }, [costMax, costMin, productSearchTerm, sortedProducts, stateFilter, stockOnly]);

  const hasActiveProductFilters = useMemo(() => {
    return (
      productSearchTerm.trim().length > 0 ||
      stateFilter !== "all" ||
      costMin.trim().length > 0 ||
      costMax.trim().length > 0 ||
      stockOnly
    );
  }, [costMax, costMin, productSearchTerm, stateFilter, stockOnly]);

  const assistantPrompt = useMemo(() => {
    return buildCategoryAssistantPrompt({
      applicationId: applicationId ?? "",
      categoryCode: category?.CategoryCode ?? categoryCode ?? "",
      categoryName: category?.CategoryName ?? "Category",
      totalProducts: products.length,
      visibleProducts: filteredProducts.length,
      productSearchTerm,
      stateFilter,
      costMin,
      costMax,
      stockOnly,
      sortOption,
      productView,
    });
  }, [
    applicationId,
    category?.CategoryCode,
    category?.CategoryName,
    categoryCode,
    costMax,
    costMin,
    stockOnly,
    filteredProducts.length,
    productSearchTerm,
    productView,
    products.length,
    sortOption,
    stateFilter,
  ]);

  const closeOfficialLinkDialog = useCallback(() => {
    setOfficialLinkDialogOpen(false);
    setOfficialLinkDraft("");
    setOfficialLinkProduct(null);
  }, []);

  const openOfficialLinkDialogForProduct = useCallback(
    (product: (typeof products)[0]) => {
      const productNumber = product.RandmarSKU ?? product.MPN ?? product.UPC ?? "";
      if (!productNumber) {
        toast({
          variant: "destructive",
          title: "Missing product identifier",
          description: "This product is missing a SKU, MPN, and UPC so it cannot be updated from here.",
        });
        return;
      }

      setOfficialLinkProduct({
        productNumber,
        title: product.Title ?? product.MPN ?? product.RandmarSKU ?? "Product",
      });
      setOfficialLinkDraft(product.OfficialManufacturerProductLink?.trim() ?? "");
      setOfficialLinkDialogOpen(true);
    },
    [toast]
  );

  const saveOfficialLink = useCallback(async () => {
    if (!applicationId || !officialLinkProduct) return;

    const trimmed = officialLinkDraft.trim();
    if (!trimmed) {
      toast({ variant: "destructive", title: "Missing link", description: "Enter a valid official product URL." });
      return;
    }

    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    try {
      const parsedUrl = new URL(normalized);
      if (!parsedUrl.hostname) throw new Error("Invalid URL");
    } catch {
      toast({ variant: "destructive", title: "Invalid link", description: "Enter a valid URL (including domain)." });
      return;
    }

    try {
      await updateProductField({
        routeManufacturerId: applicationId,
        productNumber: officialLinkProduct.productNumber,
        fieldName: "OfficialManufacturerProductLink",
        newValue: normalized,
      }).unwrap();

      toast({
        variant: "success",
        title: "Link saved",
        description: `Official link saved for ${officialLinkProduct.title}.`,
      });

      closeOfficialLinkDialog();
      await refetch();
    } catch {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "Could not save the official product link. Please try again.",
      });
    }
  }, [applicationId, closeOfficialLinkDialog, officialLinkDraft, officialLinkProduct, refetch, toast, updateProductField]);

  const productColumns = useMemo((): ColumnDef<(typeof products)[0]>[] => [
    {
      accessorKey: "Title",
      header: "Product",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex items-center gap-4">
            <ProductTableImage sku={product.RandmarSKU!} alt={product.Title ?? 'Product image'} />
            <a href={`/${applicationId}/Product/${product.RandmarSKU}`} className="font-medium truncate hover:underline">
              {product.Title}
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: "MPN",
      header: "MPN",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.MPN ?? '—',
    },
    {
      accessorKey: "UPC",
      header: "UPC",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.UPC?.trim() || "—",
    },
    {
      accessorKey: "MasterCarton",
      header: "MOQ",
      enableColumnFilter: false,
      cell: ({ row }) => (row.original.MasterCarton ? formatCount(row.original.MasterCarton) : "—"),
    },
    {
      accessorKey: "MAP",
      header: "MAP",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.MAP ? formatMoney(row.original.MAP) : '—',
    },
    {
      accessorKey: "MSRP",
      header: "MSRP",
      enableColumnFilter: false,
      cell: ({ row }) => (row.original.MSRP != null ? formatMoney(row.original.MSRP) : "—"),
    },
    {
      id: "Cost",
      header: "Cost",
      accessorFn: (row) => row.Distribution?.Cost ?? null,
      enableColumnFilter: false,
      cell: ({ row }) => (row.original.Distribution?.Cost != null ? formatMoney(row.original.Distribution.Cost) : "—"),
    },
    {
      accessorKey: "ManufacturerPercentileRank",
      header: "Mfr % Rank",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const value = row.original.ManufacturerPercentileRank;
        return typeof value === "number" ? formatCount(value) : "—";
      },
    },
    {
      id: "signals",
      header: "Signals",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { SerialNumber, AutoUpdate, State } = row.original;
        const serialEnabled = !!SerialNumber;
        const autoUpdateEnabled = !!AutoUpdate && AutoUpdate !== 0;
        const stateChip = resolveStateChip(State);

        return (
          <div className="flex items-center gap-2">
            <ProductValueChip
              variant={serialEnabled ? "info" : "outline"}
              icon={<Barcode className="h-4 w-4" aria-hidden="true" />}
              label="Serial number"
              tooltip={`Serial number: ${serialEnabled ? "Enabled" : "Disabled"}`}
            />
            <ProductValueChip
              variant={stateChip.variant}
              icon={<CircleDot className="h-4 w-4" aria-hidden="true" />}
              label="State"
              tooltip={stateChip.tooltip}
            />
            <ProductValueChip
              variant={autoUpdateEnabled ? "success" : "outline"}
              icon={<Repeat className="h-4 w-4" aria-hidden="true" />}
              label="Auto update"
              tooltip={`Auto update: ${autoUpdateEnabled ? "On" : "Off"}`}
            />
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Link to={Path.product(applicationId, row.original.RandmarSKU ?? "")}>
            <Button variant="secondary">
              <Eye className="h-5 w-5" />
              <span>View</span>
            </Button>
          </Link>
          {!row.original.OfficialManufacturerProductLink?.trim() && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openOfficialLinkDialogForProduct(row.original)}
                  aria-label="Add official product link"
                >
                  <Link2 className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">Add official product link</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add official product link</TooltipContent>
            </Tooltip>
          )}
          <ProductActions product={Product.convertFromNewProduct(row.original)} />
        </div>
      ),
    },
  ], [applicationId, openOfficialLinkDialogForProduct]);

  const categorySalesMetrics = useMemo<CategorySalesMetrics | null>(() => {
    if (!statistics.length) return null;
    const now = new Date();
    const last30Cutoff = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    last30Cutoff.setUTCDate(last30Cutoff.getUTCDate() - 30);

    let lifetimeRevenue = 0;
    let lifetimeUnits = 0;
    let last30Revenue = 0;
    let last30Units = 0;
    let lastSaleDate: Date | null = null;
    let bestDayRevenue = 0;
    let bestDayDate: Date | null = null;
    const trackedDays = new Set<string>();

    statistics.forEach((stat) => {
      const revenue = stat?.ExtendedPrice ?? 0;
      const units = stat?.Quantity ?? 0;
      const statDate = parseStatisticDate(stat?.Day);
      lifetimeRevenue += revenue;
      lifetimeUnits += units;

      if (statDate) {
        const isoDay = statDate.toISOString().slice(0, 10);
        trackedDays.add(isoDay);

        if ((revenue > 0 || units > 0) && (!lastSaleDate || statDate > lastSaleDate)) {
          lastSaleDate = statDate;
        }

        if (revenue > bestDayRevenue) {
          bestDayRevenue = revenue;
          bestDayDate = statDate;
        }

        if (statDate >= last30Cutoff) {
          last30Revenue += revenue;
          last30Units += units;
        }
      }
    });

    const avgDailyRevenue = trackedDays.size ? lifetimeRevenue / trackedDays.size : 0;

    return {
      lifetimeRevenue,
      lifetimeUnits,
      avgDailyRevenue,
      lastSaleDate,
      bestDayRevenue,
      bestDayDate,
      last30Revenue,
      last30Units,
      daysTracked: trackedDays.size,
    };
  }, [statistics]);

  useEffect(() => {
    if (!isFetching && category) {
      setLastRefreshedAt(new Date());
    }
  }, [category, isFetching]);

  useEffect(() => {
    const previousTitle = document.title;
    const label = category?.CategoryName || categoryCode || "Category";
    document.title = `Category · Manufacturer · ${label}`;

    return () => {
      document.title = previousTitle;
    };
  }, [category?.CategoryName, categoryCode]);

  useEffect(() => {
    if (category) {
      setFormData({
        CategoryName: category.CategoryName ?? '',
        Niche: category.Niche ?? '',
        Starred: category.Starred ?? false,
        AutoReorder: category.AutoReorder ?? false,
        QualificationRequired: category.QualificationRequired ?? false,
        MinimumOrder: category.MinimumOrder ?? null,
        LeadTime: category.LeadTime ?? null,
        WeeksOfStocking: category.WeeksOfStocking ?? null,
      });
    }
  }, [category]);

  useEffect(() => {
    if (category?.CategoryCode) {
      setImageExists(false);
      setImageUrl(`${Path.categoryImage(applicationId!, category.CategoryCode)}?t=${new Date().getTime()}`);
    } else {
      setImageExists(false);
      setImageUrl("");
    }
  }, [category, applicationId]);

  const [updateCategoryField] = usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeFieldNameMutation();
  const [updateCategoryImage, { isLoading: isImageUpdating }] = usePostV4ManufacturerByRouteManufacturerIdCategoryAndCategoryCodeImageMutation();

  const handleImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      await updateCategoryImage({
        routeManufacturerId: applicationId!,
        categoryCode: category!.CategoryCode!,
        body: { file },
      }).unwrap();
      toast({ variant: "success", title: "Success", description: "Category image updated." });
      setImageUrl(`${Path.categoryImage(applicationId!, category!.CategoryCode!)}?t=${new Date().getTime()}`);
    } catch {
      toast({ variant: "destructive", title: "Image Upload Failed", description: "Please ensure the file is a valid image." });
    }
  };

  const handleInputChange = (fieldName: keyof typeof formData, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const hasChanges = useMemo(() => {
    if (!category) return false;
    return (
      formData.CategoryName !== (category.CategoryName ?? "") ||
      formData.Niche !== (category.Niche ?? "") ||
      formData.Starred !== (category.Starred ?? false) ||
      formData.AutoReorder !== (category.AutoReorder ?? false) ||
      formData.QualificationRequired !== (category.QualificationRequired ?? false) ||
      (formData.MinimumOrder ?? null) !== (category.MinimumOrder ?? null) ||
      (formData.LeadTime ?? null) !== (category.LeadTime ?? null) ||
      (formData.WeeksOfStocking ?? null) !== (category.WeeksOfStocking ?? null)
    );
  }, [formData, category]);

  const handleSave = async () => {
    if (!hasChanges) return;
    setIsSaving(true);
    const changedFields: { fieldName: string; newValue: string }[] = [];

    (Object.keys(formData) as Array<keyof typeof formData>).forEach((key) => {
      let originalValue: string | number | boolean | null;
      switch (key) {
        case 'Starred':
        case 'AutoReorder':
        case 'QualificationRequired':
          originalValue = category?.[key] ?? false;
          break;
        case 'MinimumOrder':
        case 'LeadTime':
        case 'WeeksOfStocking':
          originalValue = category?.[key] ?? null;
          break;
        default:
          originalValue = category?.[key] ?? '';
          break;
      }

      if (formData[key] !== originalValue) {
        const newValueString = formData[key] === null ? '' : String(formData[key]);
        changedFields.push({ fieldName: key, newValue: newValueString });
      }
    });

    if (changedFields.length === 0) {
      setIsSaving(false);
      return;
    }

    const savePromises = changedFields.map(field =>
      updateCategoryField({
        routeManufacturerId: applicationId!,
        categoryCode: category!.CategoryCode!,
        fieldName: field.fieldName,
        newValue: field.newValue,
      }).unwrap()
    );

    try {
      await Promise.all(savePromises);
      toast({ variant: 'success', title: 'Success', description: 'Changes saved successfully.' });
      refetch();
    } catch {
      toast({ variant: 'destructive', title: 'Save Failed', description: 'One or more fields could not be saved.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetReorderSettings = async () => {
    setIsResetting(true);
    const fieldsToReset: ('MinimumOrder' | 'LeadTime' | 'WeeksOfStocking')[] = [];
    if (category?.MinimumOrder != null) fieldsToReset.push('MinimumOrder');
    if (category?.LeadTime != null) fieldsToReset.push('LeadTime');
    if (category?.WeeksOfStocking != null) fieldsToReset.push('WeeksOfStocking');

    if (fieldsToReset.length === 0) {
      setFormData((prev) => ({
        ...prev,
        MinimumOrder: null,
        LeadTime: null,
        WeeksOfStocking: null,
      }));
      setIsResetting(false);
      return;
    }

    const resetPromises = fieldsToReset.map((fieldName) =>
      updateCategoryField({
        routeManufacturerId: applicationId!,
        categoryCode: category!.CategoryCode!,
        fieldName: fieldName,
        newValue: '',
      }).unwrap()
    );

    try {
      await Promise.all(resetPromises);
      toast({ variant: 'success', title: 'Success', description: 'Reorder settings reset to manufacturer defaults.' });
      refetch();
    } catch {
      toast({ variant: 'destructive', title: 'Reset Failed', description: 'Could not reset reorder settings.' });
    } finally {
      setIsResetting(false);
    }
  };

  if (isLoading || isAccountLoading) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <Skeleton className="h-10 w-1/3" />
        <div className="flex gap-6">
          <div className="w-1/3"><Skeleton className="h-64 w-full" /></div>
          <div className="w-2/3"><Skeleton className="h-64 w-full" /></div>
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (isError || !category) {
    return (
      <Card className="m-4 p-6 md:m-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Could not load category details. Please try again.</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <>
      <RmPageHeader
        title={category.CategoryName || "Category"}
        subtitle={`Code: ${category.CategoryCode || categoryCode || "—"}`}
        rightTopActions={(
          <DataRefreshAssistantCluster
            lastRefreshAt={lastRefreshedAt}
            isRefreshing={isFetching}
            onRefresh={async () => {
              await refetch();
            }}
            assistantPromptOptions={{ prompt: assistantPrompt }}
          />
        )}
      />
      <RmBodyZone className="space-y-6 pb-10">
        <div className="space-y-6">
          <Card className="@container rounded-2xl border border-slate-100 shadow-sm">
            <CardHeader className="space-y-2 pb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">Category</p>
                <CardTitle className="text-xl font-medium text-slate-900">Information &amp; Settings</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground">
                Update how this category is presented to resellers and how it behaves in replenishment workflows.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Image</p>
                    <p className="text-sm text-muted-foreground">Appears in the reseller catalog for this category.</p>
                  </div>

                  <div className="relative aspect-square overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
                    {!imageExists && <CategoryImagePlaceholder />}
                    <img
                      src={imageUrl}
                      alt={imageExists ? `Image of ${category.CategoryName}` : ""}
                      className={`h-full w-full object-contain transition-opacity duration-300 ${imageExists ? "opacity-100" : "opacity-0"
                        }`}
                      onLoad={() => setImageExists(true)}
                      onError={() => setImageExists(false)}
                    />
                  </div>

                  <input
                    id="categoryImage"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                  <Button
                    className="w-full"
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isImageUpdating}
                  >
                    {isImageUpdating ? <InlineSpinner className="mr-2" decorative /> : <Upload className="mr-2 h-4 w-4" />}
                    {isImageUpdating ? "Uploading..." : "Upload image"}
                  </Button>
                  <p className="text-xs text-slate-500">PNG or JPG recommended. Square images look best.</p>
                </div>

                <div className="space-y-6">
                  <div className="grid gap-6 @2xl:grid-cols-2">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Basics</p>
                      <div className="mt-4 space-y-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="categoryName">Name</Label>
                          <Input
                            id="categoryName"
                            value={formData.CategoryName}
                            onChange={(e) => handleInputChange("CategoryName", e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="niche">Niche</Label>
                          <Input id="niche" value={formData.Niche} onChange={(e) => handleInputChange("Niche", e.target.value)} />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Visibility</p>
                      <div className="mt-4 space-y-4">
                        <div className="flex items-center justify-between gap-4">
                          <Label htmlFor="starred-switch" className="pr-2">
                            Starred Category
                          </Label>
                          <Switch
                            id="starred-switch"
                            checked={formData.Starred}
                            onCheckedChange={(checked) => handleInputChange("Starred", checked)}
                          />
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <Label htmlFor="reorder-switch" className="pr-2">
                            Auto Reorder
                          </Label>
                          <Switch
                            id="reorder-switch"
                            checked={formData.AutoReorder}
                            onCheckedChange={(checked) => handleInputChange("AutoReorder", checked)}
                          />
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <Label htmlFor="qualified-switch" className="pr-2">
                            Qualified Resellers Only
                          </Label>
                          <Switch
                            id="qualified-switch"
                            checked={formData.QualificationRequired}
                            onCheckedChange={(checked) => handleInputChange("QualificationRequired", checked)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-100 bg-white">
                    <Accordion
                      type="single"
                      collapsible
                      className="w-full"
                      defaultValue={hasCustomReorderSettings ? "reorder-settings" : undefined}
                    >
                      <AccordionItem value="reorder-settings" className="border-0 px-4">
                        <AccordionTrigger className="py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Reorder Settings</span>
                            {hasCustomReorderSettings && (
                              <div className="flex items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                <AlertTriangle className="h-3 w-3" />
                                Category Override
                              </div>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-4 pt-2">
                          <div className="grid grid-cols-1 gap-y-4 gap-x-6 @lg:grid-cols-3">
                            <div className="space-y-1.5">
                              <Label htmlFor="weeksOfStocking">Weeks of Stock</Label>
                              <Input
                                id="weeksOfStocking"
                                type="number"
                                placeholder={`Default: ${account?.DefaultWeeksOfStocking ?? '...'}`}
                                value={formData.WeeksOfStocking ?? ''}
                                onChange={(e) => handleInputChange('WeeksOfStocking', e.target.value === '' ? null : e.target.valueAsNumber)}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="leadTime">Lead Time (days)</Label>
                              <Input
                                id="leadTime"
                                type="number"
                                placeholder={`Default: ${account?.DefaultLeadTime ?? '...'}`}
                                value={formData.LeadTime ?? ''}
                                onChange={(e) => handleInputChange('LeadTime', e.target.value === '' ? null : e.target.valueAsNumber)}
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label htmlFor="minimumOrder">Minimum Order ($)</Label>
                              <Input
                                id="minimumOrder"
                                type="number"
                                placeholder={`Default: ${account?.DefaultMinimumOrder ?? '...'}`}
                                value={formData.MinimumOrder ?? ''}
                                onChange={(e) => handleInputChange('MinimumOrder', e.target.value === '' ? null : e.target.valueAsNumber)}
                              />
                            </div>
                          </div>
                          <div className="mt-6 flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleResetReorderSettings}
                              disabled={!hasCustomReorderSettings || isResetting}
                            >
                              {isResetting ? <InlineSpinner className="mr-2" decorative /> : <RotateCcw className="mr-2 h-4 w-4" />}
                              Reset to Defaults
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>

                  <Separator />

                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <p className="text-sm text-muted-foreground">
                      Changes are saved to this category and applied immediately.
                    </p>
                    <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
                      {isSaving ? (
                        <>
                          <InlineSpinner className="mr-2" decorative />
                          Saving...
                        </>
                      ) : (
                        "Save changes"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {(activeOrders.length > 0 || categorySalesMetrics) && (
            <section className="space-y-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900">Category sales overview</h2>
                  <p className="text-sm text-muted-foreground">
                    Tracking {formatPlural(products.length, "product")} across{" "}
                    {formatPlural(categorySalesMetrics?.daysTracked ?? statistics.length, "day")} of recent sales data.
                  </p>
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">Sales insights</span>
              </div>
              <div className="grid gap-5 lg:grid-cols-2">
                <Card className="@container">
                  <CardHeader className="pb-4">
                    <CardTitle>Category sales metrics</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Rolling 30-day snapshot plus lifetime performance.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {categorySalesMetrics ? (
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Revenue (30d)</p>
                          <p className="text-2xl font-bold text-foreground">{formatMoney(categorySalesMetrics.last30Revenue)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatPlural(categorySalesMetrics.last30Units, "unit")} shipped
                          </p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lifetime revenue</p>
                          <p className="text-2xl font-bold text-foreground">{formatMoney(categorySalesMetrics.lifetimeRevenue)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatPlural(categorySalesMetrics.lifetimeUnits, "unit")} sold
                          </p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Avg daily revenue</p>
                          <p className="text-2xl font-bold text-foreground">{formatMoney(categorySalesMetrics.avgDailyRevenue)}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatPlural(categorySalesMetrics.daysTracked, "day")} of data
                          </p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Last sale</p>
                          <p className="text-2xl font-bold text-foreground">{formatDateLabel(categorySalesMetrics.lastSaleDate)}</p>
                          <p className="text-xs text-muted-foreground">
                            Best day: {formatMoney(categorySalesMetrics.bestDayRevenue)} on{" "}
                            {formatDateLabel(categorySalesMetrics.bestDayDate)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-muted-foreground">
                        Not enough sales data yet. We’ll surface metrics as soon as orders start flowing through this category.
                      </div>
                    )}
                  </CardContent>
                </Card>
                <ActiveOrdersCard orders={activeOrders} />
              </div>
            </section>
          )}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="inline-flex h-auto items-center justify-center rounded-none border-b bg-transparent p-0">
              <TabsTrigger
                value="statistics" disabled={statistics.length === 0}
                className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
              >
                Statistics
              </TabsTrigger>
              <TabsTrigger
                value="products" disabled={products.length === 0}
                className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
              >
                Products ({products.length})
              </TabsTrigger>
              <TabsTrigger
                value="margins"
                className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
              >
                Margins &amp; Operations
              </TabsTrigger>
              <TabsTrigger
                value="orders" disabled={activeOrders.length === 0}
                className="relative h-12 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none hover:text-foreground data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none cursor-pointer"
              >
                Active Orders ({activeOrders.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="statistics" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Sales Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <SalesChart statistics={statistics} chartHeight={300} />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="products" className="mt-4">
              <TooltipProvider>
                <section
                  className="my-4 space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
                  aria-label="Product filters"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div className="grid flex-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="category-products-search"
                          className="text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          Search
                        </Label>
                        <div className="relative">
                          <Search
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                            aria-hidden="true"
                          />
                          <Input
                            id="category-products-search"
                            value={productSearchTerm}
                            onChange={(event) => setProductSearchTerm(event.target.value)}
                            placeholder="Title, SKU, MPN, or UPC"
                            className="h-11 rounded-lg border-slate-200 bg-white pl-9 text-[15px]"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label
                          htmlFor="category-products-state"
                          className="text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          State
                        </Label>
                        <Select value={stateFilter} onValueChange={setStateFilter}>
                          <SelectTrigger id="category-products-state" className="h-11 rounded-lg border-slate-200 bg-white">
                            <SelectValue placeholder="All states" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All states</SelectItem>
                            {stateFilterOptions.length > 0 ? <Separator /> : null}
                            {stateFilterOptions.map((state) => (
                              <SelectItem key={`category-state-${state}`} value={state}>
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cost</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            inputMode="decimal"
                            placeholder="Min"
                            value={costMin}
                            onChange={(event) => setCostMin(event.target.value)}
                            className="h-11 rounded-lg border-slate-200 bg-white text-[15px]"
                          />
                          <Input
                            inputMode="decimal"
                            placeholder="Max"
                            value={costMax}
                            onChange={(event) => setCostMax(event.target.value)}
                            className="h-11 rounded-lg border-slate-200 bg-white text-[15px]"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="category-products-stock-only" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Availability
                        </Label>
                        <label
                          htmlFor="category-products-stock-only"
                          className="flex h-11 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-[15px] text-slate-700"
                        >
                          <Checkbox
                            id="category-products-stock-only"
                            checked={stockOnly}
                            onCheckedChange={(checked) => setStockOnly(checked === true)}
                          />
                          <span>Stock Only</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between lg:flex-none lg:justify-end">
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="category-products-sort"
                          className="text-xs font-semibold uppercase tracking-wide text-slate-500"
                        >
                          Sort by
                        </Label>
                        <Select value={sortOption} onValueChange={setSortOption}>
                          <SelectTrigger id="category-products-sort" className="h-11 w-full rounded-lg border-slate-200 bg-white sm:w-[240px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentile">Best Selling</SelectItem>
                            <SelectItem value="name">Name</SelectItem>
                            <Separator />
                            <SelectItem value="price-asc">MAP: Low to High</SelectItem>
                            <SelectItem value="price-desc">MAP: High to Low</SelectItem>
                            <Separator />
                            <SelectItem value="mpn-asc">MPN: Low to High</SelectItem>
                            <SelectItem value="mpn-desc">MPN: High to Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex flex-col gap-2 sm:items-end">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-11 rounded-xl"
                          onClick={resetProductFilters}
                          disabled={!hasActiveProductFilters}
                        >
                          Reset filters
                        </Button>
                        <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 p-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant={productView === "grid" ? "secondary" : "ghost"}
                                size="icon"
                                className="h-9 w-9 rounded-lg"
                                onClick={() => setProductView("grid")}
                              >
                                <LayoutGrid className="h-4 w-4" />
                                <span className="sr-only">Grid View</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Grid View</p>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant={productView === "table" ? "secondary" : "ghost"}
                                size="icon"
                                className="h-9 w-9 rounded-lg"
                                onClick={() => setProductView("table")}
                              >
                                <List className="h-4 w-4" />
                                <span className="sr-only">Table View</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Table View</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-slate-600">
                    Showing {formatCount(filteredProducts.length)} of {formatCount(products.length)} products
                  </div>
                </section>

                {productView === 'grid' ? (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {filteredProducts.map((product) => (
                      <ProductCard
                        applicationId={applicationId!}
                        key={`product_grid_${product.RandmarSKU}`}
                        product={product}
                        viewProductLink={`/${applicationId}/Product/${product.RandmarSKU}`}
                        customAction={
                          <ProductActions
                            key={`product_actions_grid_${product.RandmarSKU}`}
                            product={Product.convertFromNewProduct(product)}
                          />
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle>Products</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <DataTable
                        columns={productColumns}
                        data={filteredProducts}
                        uniqueValues={{}}
                        initialSort={[{ id: "Title", desc: false }]}
                      />
                    </CardContent>
                  </Card>
                )}

                <Dialog
                  open={officialLinkDialogOpen}
                  onOpenChange={(open) => {
                    if (!open) closeOfficialLinkDialog();
                    else setOfficialLinkDialogOpen(true);
                  }}
                >
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add official product link</DialogTitle>
                      <DialogDescription>
                        {officialLinkProduct?.title ? `Set the official link for ${officialLinkProduct.title}.` : "Set the official manufacturer product link."}
                      </DialogDescription>
                    </DialogHeader>
                    <form
                      className="space-y-4"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void saveOfficialLink();
                      }}
                    >
                      <div className="space-y-2">
                        <Label htmlFor="official-product-link">Official product URL</Label>
                        <Input
                          id="official-product-link"
                          placeholder="https://example.com/product"
                          value={officialLinkDraft}
                          onChange={(event) => setOfficialLinkDraft(event.target.value)}
                          autoFocus
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="secondary" onClick={closeOfficialLinkDialog} disabled={isOfficialLinkUpdating}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={isOfficialLinkUpdating}>
                          {isOfficialLinkUpdating ? <InlineSpinner className="mr-2" decorative /> : null}
                          <span>{isOfficialLinkUpdating ? "Saving" : "Save link"}</span>
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </TooltipProvider>
            </TabsContent>
            <TabsContent value="margins" className="mt-4">
              <div className="space-y-4">
                <Card>
                  <NewMarginsGrid category={category} />
                </Card>
                {operationValues && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Location Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead />
                            <TableHead className="text-center">Total</TableHead>
                            {["MTL", "TOR", "VAN", "EDM"].map((loc) => (
                              <TableHead key={loc} className="text-center">{loc}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-semibold">Inventory Value</TableCell>
                            <TableCell className="text-center font-semibold">{formatMoney(operationValues.totals.inventory)}</TableCell>
                            {["MTL", "TOR", "VAN", "EDM"].map((loc) => (
                              <TableCell key={loc} className="text-center">
                                {formatMoney(operationValues.byLocation?.find(d => d.Location === loc)?.InventoryValue || 0)}
                              </TableCell>
                            ))}
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-semibold">On Purchase Orders</TableCell>
                            <TableCell className="text-center font-semibold">{formatMoney(operationValues.totals.purchaseOrder)}</TableCell>
                            {["MTL", "TOR", "VAN", "EDM"].map((loc) => (
                              <TableCell key={loc} className="text-center">
                                {formatMoney(operationValues.byLocation?.find(d => d.Location === loc)?.PurchaseOrderValue || 0)}
                              </TableCell>
                            ))}
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-semibold">On Sales Orders</TableCell>
                            <TableCell className="text-center font-semibold">{formatMoney(operationValues.totals.salesOrder)}</TableCell>
                            {["MTL", "TOR", "VAN", "EDM"].map((loc) => (
                              <TableCell key={loc} className="text-center">
                                {formatMoney(operationValues.byLocation?.find(d => d.Location === loc)?.SalesOrderValue || 0)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
            <TabsContent value="orders" className="mt-4">
              <OrdersCard applicationId={applicationId ?? ""} orders={activeOrders} />
            </TabsContent>
          </Tabs>
        </div>
      </RmBodyZone>
    </>
  );
}
